import axios from 'axios';
import { getAgentsRequestQueryParams, getAgentsPayloadParams } from '@/utils/agentsApi';

/**
 * The result a guest UI is given when a tool call does not go through.
 *
 * A normal MCP error result rather than a thrown request: from the guest's
 * point of view nothing went wrong with the transport — it asked for something
 * it is not allowed to do, and that is an answer it should render.
 */
function toolErrorResult(message) {
  return {
    content: [{ type: 'text', text: message }],
    isError: true
  };
}

/** The service's own explanation if it sent one, otherwise the transport's. */
export function describeToolError(err) {
  return err?.response?.data?.detail || err?.message || 'Tool call failed';
}

/**
 * Creates handlers that translate AppBridge requests to Agents API calls.
 *
 * Since cx-portal uses the Agents API as an MCP proxy (no direct MCP client access),
 * we need to manually implement handlers that call the Agents API endpoints.
 *
 * @param {string} agentsUrl - Base URL for the Agents API
 * @param {string} accessToken - Bearer token for authentication
 * @param {string} cid - Client ID for the current client
 * @param {Object} [rootState] - Vuex root state (for region/cloud query params)
 * @param {Object} [options]
 * @param {Function} [options.requestApproval] - Shows the approval prompt for a
 *   tool the client set to `ask`, writes the user's answer, and resolves with
 *   `'allow_once'` or `'deny'`. Without it an `ask` tool simply cannot be run
 *   from a guest UI, which is the right fallback — running it anyway would
 *   defeat the setting.
 * @param {string} [options.resourceId] - Chat this UI is rendered in, so an
 *   approval files with that conversation rather than on its own.
 * @returns {Object} Handler functions for AppBridge
 */
export function createAgentsApiHandlers(agentsUrl, accessToken, cid, rootState, options = {}) {
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
  const agentsPayload = () => getAgentsPayloadParams(rootState);

  return {
    /**
     * Read a resource from the MCP server via Agents API.
     * Maps to: POST /v1/aquabot/resource
     *
     * @param {Object} params - Request parameters
     * @param {string} params.uri - Resource URI to fetch
     * @returns {Promise<Object>} Resource contents in MCP format
     */
    async readResource(params) {
      const response = await axios.post(
        `${agentsUrl}/v1/aquabot/resource`,
        { uri: params.uri, cid, ...agentsPayload() },
        { headers, timeout: 60000 }
      );

      // Normalize response to MCP ReadResourceResult format
      const text = response.data.text || response.data.content ||
        (typeof response.data === 'string' ? response.data : JSON.stringify(response.data));

      return {
        contents: [{
          uri: params.uri,
          mimeType: 'text/html;profile=mcp-app',
          text
        }]
      };
    },

    /**
     * Call a tool on the MCP server via Agents API.
     * Maps to: POST /v1/aquabot/tool
     *
     * Tools the client set to `ask` answer 202 with a `toolUseId` instead of
     * running. This walks that handshake: show the prompt, write the answer,
     * repeat the call with the handle. The guest UI sees one `callTool` that
     * took a while — it is never told an approval happened, because a guest UI
     * must not be able to tell the difference between a tool it may run freely
     * and one a person had to agree to.
     *
     * The service binds each approval to this exact user, server, tool and set
     * of arguments, and spends it once, so nothing here has to be trusted for
     * that to hold.
     *
     * @param {Object} params - Request parameters
     * @param {string} params.name - Tool name
     * @param {Object} params.arguments - Tool arguments
     * @returns {Promise<Object>} Tool call result
     */
    async callTool(params) {
      const body = {
        name: params.name,
        arguments: params.arguments || {},
        cid,
        ...agentsPayload()
      };
      if (options.resourceId) body.resourceId = options.resourceId;

      // Two passes at most: ask, then run. A second 202 would mean the approval
      // did not take, and looping on it would spin forever.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const response = await axios.post(
          `${agentsUrl}/v1/aquabot/tool`,
          body,
          { headers, timeout: 120000 }
        );

        if (response.status !== 202) return response.data;

        const prompt = response.data || {};
        const label = prompt.rawToolName || params.name;

        if (!options.requestApproval) {
          return toolErrorResult(
            `"${label}" needs approval before it can run, and this view cannot ask `
            + 'for it. Run it from a PAICE chat instead.'
          );
        }

        // Resolves only once the answer has been written, so the repeat below
        // can rely on the decision being on record.
        const decision = await options.requestApproval(prompt);
        if (decision !== 'allow_once') {
          return toolErrorResult(`You did not allow "${label}", so it did not run.`);
        }

        body.toolUseId = prompt.toolUseId;
        body.resourceId = prompt.resourceId;
      }

      return toolErrorResult(
        'The approval for this tool could not be used. Try running it again.'
      );
    }
  };
}

/**
 * Fetch the list of available MCP tools with their metadata.
 * Tools with _meta.ui.resourceUri have associated UI visualizations.
 *
 * @param {string} agentsUrl - Base URL for the Agents API
 * @param {string} accessToken - Bearer token for authentication
 * @param {Object} [rootState] - Vuex root state (for region/cloud query params)
 * @returns {Promise<Object>} Object with tools array and toolsWithUi array
 */
export async function fetchMcpTools(agentsUrl, accessToken, rootState, cid) {
  const response = await axios.get(
    `${agentsUrl}/v1/aquabot/tools?cid=${cid}`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      params: getAgentsRequestQueryParams(rootState),
      timeout: 30000
    }
  );

  return response.data;
}

/**
 * Extract UI resource URI from tool metadata.
 * Checks both nested format (_meta.ui.resourceUri) and flat format (_meta["ui/resourceUri"]).
 *
 * @param {Object} tool - Tool object with potential _meta property
 * @returns {string|null} UI resource URI or null if not present
 */
export function getToolUiResourceUri(tool) {
  if (!tool || !tool._meta) {
    return null;
  }

  // Try nested format first: _meta.ui.resourceUri
  if (tool._meta.ui && tool._meta.ui.resourceUri) {
    const uri = tool._meta.ui.resourceUri;
    if (typeof uri === 'string' && uri.startsWith('ui://')) {
      return uri;
    }
  }

  // Try flat format: _meta["ui/resourceUri"]
  const flatUri = tool._meta['ui/resourceUri'];
  if (typeof flatUri === 'string' && flatUri.startsWith('ui://')) {
    return flatUri;
  }

  return null;
}
